<template>
    <div>
        <Hero v-if="page && CAT_INTEGRATIONS.length" :title="categoryTitle" :highlight-words="heroHighlightWords"
            :subtitle="heroSubtitle" :buttons="heroButtons" />

        <div class="w-full flex flex-col items-center gap-4 pb-12" v-if="page && category !== 'scim' && category">
            <img :src="`/images/categories/${category}.gif`" :alt="page?.name || ''" width="1344" height="720"
                class="w-full h-auto max-w-[1344px]" />
        </div>

        <div class="w-full flex flex-col items-center gap-4">
            <section class="w-2/3">
                <IntegrationsList :cats="page?.filterCategories" :integrations="CAT_INTEGRATIONS" />
            </section>

            <Modern
                :deep-normalized-title="category === 'scim' ? 'Unified API, deeply normalized' : 'Unified APIs, deeply normalized'" />

            <section>
                <Benefits bottom_image title="Get integrations from backlog to production in days"
                    :benefits="benefits" />
            </section>


            <SeamlessAuth v-if="category !== 'auth'" />

            <BusinessLogin v-if="category !== 'auth'" />

            <section class="py-24 max-w-full">
                <Customers />
            </section>

            <section class="max-w-screen-lg mx-auto w-full py-12 px-6" v-if="category === 'calendar'">
                <div class="grid md:grid-cols-2 gap-24 items-start">
                    <div class="space-y-6">
                        <h2 class="text-4xl font-bold text-white">Seamless Scheduling, no extra work</h2>
                        <p class="text-neutral-50">

                            Unified.to removes the complexity of scheduling meetings in your application,
                            so you can focus on
                            building your product, not managing scheduling.
                        </p>
                        <p class="text-neutral-50">
                            Just use our pre-built VueJS or React components, and we will handle the rest. Using our
                            Unified Calendar API to fetch FreeBusy times and to schedule meetings is a breeze, so you
                            don't have to build your own scheduling solution.
                        </p>

                        <a href="https://app.unified.to/embed?tab=CalendarScheduler"
                            class="text-blue-500 flex items-center gap-2">
                            See example
                            <Icon name="ic:round-keyboard-arrow-right" />
                        </a>
                    </div>
                    <div>
                        <img src="/images/unified-calendar-config.png" alt="Calendar Scheduler configuration"
                            class="w-full h-auto object-contain max-h-96" />
                    </div>
                </div>
            </section>

            <section class="-mb-12 py-12 max-w-full" v-if="category_usecases.length">
                <UseCases title="Fresh data to fuel countless use cases"
                    subtitle="Power AI copilots, insights, predictive analysis, and automation with a modern integration infrastructure that scales alongside you."
                    :usecases="category_usecases" :hide_button="true" />
            </section>

            <section class="max-w-screen-lg mx-auto px-6 py-12 text-center" v-if="page?.kind === 'meta'">
                <p class="text-neutral-100 text-lg">
                    {{ page?.name }} includes
                    <template v-for="(child, i) in childCategoryLinks" :key="child.slug">
                        <span v-if="i > 0">{{ i === childCategoryLinks.length - 1 ? ', and ' : ', ' }}</span>
                        <NuxtLink :to="`/${child.slug}`" class="text-blue-500 hover:underline">{{ child.name }}
                        </NuxtLink>
                    </template>
                    APIs.
                </p>
            </section>

            <section class="max-w-screen-2xl mx-auto mt-20" v-if="objectCategories.length">
                <CategorySection :cats="objectCategories" :label="page?.name" />
                <!-- <HomeSection :label="(category as TIntegrationCategory)" :desc="SECTION.desc" :code="SECTION.code" :no-logos="true" /> -->
            </section>

            <!--
        <Benefits center :small="true" :title="`Benefits for ${INDUSTRIES[category]} applications`" :benefits="[
            {
                title: 'Elevate user workflows',
                body: BENEFIT1[category],
                icon: 'material-symbols:elevation-outline',
            },
            {
                title: 'Build better products',
                body: BENEFIT2[category],
                icon: 'material-symbols:database-outline',
            },
            {
                title: 'Become a standout',
                body: 'Instantly differentiate your product and grow your customer base by offering a complete integrations ecosystem.',
                icon: 'material-symbols:stars-outline',
            },
        ]" /> -->

            <section>
                <Testimonials :delay="6000" :limit="6" />
            </section>
        </div>

        <CategogyArticles v-if="page?.kind !== 'meta'" :category="category" />

        <Hero title="Integrate. Faster." heading-level="h2" :highlight-words="['faster.']" :buttons="BUTTONS" />
    </div>
</template>

<script lang="ts">
import { INTEGRATION_CATEGORIES as CATEGORIES, INTEGRATIONS } from '~/services/integrations';
import { CAT_SECTIONS } from '~/data/sections';
import { USE_CASES } from '~/data/use_cases';
import { getCategoryPage } from '~/services/metaCategories';
import { setTitle } from '~~/services/common';

export default {
    setup() {
        const route = useRoute();
        const category = String(route.params.category);
        const page = getCategoryPage(category);
        if (!page) {
            throw createError({ statusCode: 404, statusMessage: 'Category not found' });
        }
        setTitle(`Unified ${page.name} APIs for Developers | Unified.to`, page.seo_description);
        return {
            category, page, BUTTONS: [
                { link: 'https://app.unified.to', title: 'Get started free' },
                { link: '/contact', title: 'Speak to an expert', icon: 'ic:round-keyboard-arrow-right' }
            ]
        };
    },
    computed: {
        CAT_INTEGRATIONS() {
            if (!this.page) return [];
            return INTEGRATIONS.filter((i) => this.page.filterCategories.some((c) => i.categories.includes(c)));
        },
        objectCategories(): Array<keyof typeof CATEGORIES> {
            if (!this.page || this.category === 'auth' || this.category === 'scim') return [];
            if (this.page.kind === 'meta') {
                return this.page.filterCategories as Array<keyof typeof CATEGORIES>;
            }
            return [this.category as keyof typeof CATEGORIES];
        },
        categoryTitle(): string {
            if (!this.page) return '';
            if (this.page.kind === 'meta') {
                return `Read and write ${this.page.data_type} data in real-time`;
            }
            switch (this.category as keyof typeof CATEGORIES) {
                case 'scim':
                    return 'SCIM API';
                case 'repo':
                    return 'Unified File Repository API';
                case 'auth':
                    return `Unified ${CATEGORIES[this.category as keyof typeof CATEGORIES].name} API`;
                case 'genai':
                    return 'Unified Generative LLM API for AI applications';
                default:
                    return `Read and write ${this.page.data_type} data in real-time`;
            }
        },
        category_usecases() {
            if (!this.page) return [];
            return USE_CASES.filter((use_case) => use_case.category === this.category)
                .slice(0, 4)
                .map((use_case) => ({
                    ...use_case,
                    category: this.page!.name,
                }));
        },
        categorySequenceText(): string {
            const categories = Object.keys(CATEGORIES) as Array<keyof typeof CATEGORIES>;
            const currentIndex = categories.indexOf(this.category as keyof typeof CATEGORIES);

            if (!this.page || this.page.kind === 'meta' || currentIndex === -1) {
                return `${CATEGORIES.crm.name} to ${CATEGORIES.accounting.name} and ${CATEGORIES.messaging.name}`;
            }

            const prevCategory = (currentIndex > 0 ? categories[currentIndex - 1] : categories[categories.length - 1])!;
            const nextCategory = (currentIndex < categories.length - 1 ? categories[currentIndex + 1] : categories[0])!;

            return `${CATEGORIES[prevCategory].name} to ${CATEGORIES[this.category as keyof typeof CATEGORIES].name} and ${CATEGORIES[nextCategory].name}`;
        },
        childCategoryLinks() {
            if (this.page?.kind !== 'meta') return [];
            return this.page.filterCategories.map((cat) => ({
                slug: cat,
                name: CATEGORIES[cat as keyof typeof CATEGORIES].name,
            }));
        },
        heroSubtitle(): string {
            if (!this.page) return '';
            if (this.category === 'scim') {
                return 'Provision, update, and deprovision users and groups through SCIM 2.0 endpoints you have already built. Works across HR and directory integrations regardless of whether the underlying API supports SCIM.';
            }
            const countPrefix = this.CAT_INTEGRATIONS.length > 9 ? `${this.CAT_INTEGRATIONS.length}+ ` : '';
            return `Launch ${countPrefix}pre-built ${this.page.name} integrations today and access normalized ${this.page.data_type} data with zero maintenance — all through a unified API.`;
        },
        heroButtons() {
            return [
                {
                    link: 'https://app.unified.to',
                    title: 'Get started free',
                },
                {
                    title: 'Speak to an expert',
                    link: '/contact',
                    icon: 'ic:round-keyboard-arrow-right',
                },
            ];
        },
        heroHighlightWords(): string[] {
            if (!this.page) return [];
            return [this.page.data_type, this.page.name];
        },
        benefits() {
            return [
                {
                    title: '1. Activate integrations',
                    body:
                        this.category === 'scim'
                            ? '<strong>Activate HR &amp; Directory integrations</strong> in your workspace. SCIM support comes with them.'
                            : `Enable integrations across ${Object.keys(CATEGORIES).length} API categories, from ${this.categorySequenceText}`,
                    image_url: '/images/Step-1-Activate.webp',
                },
                {
                    title: '2. Launch integrations',
                    body:
                        this.category === 'scim'
                            ? `<strong>Point your SCIM client at Unified's endpoints.</strong> Existing implementations work unchanged.`
                            : 'Build, test, and launch multiple integrations simultaneously with only a few lines of code.',
                    image_url: '/images/Step-2-Launch.webp',
                },
                {
                    title: '3. Authorize integrations',
                    body:
                        this.category === 'scim'
                            ? '<strong>Authorize customer accounts</strong> with the embedded authorization component.'
                            : 'Embed our authorization component to let customers easily link their accounts.',
                    image_url: '/images/Step-3-Authorize.webp',
                },
            ];
        },
        SECTION() {
            return CAT_SECTIONS.find((s) => s?.cat === this.category);
        },
    },
};
</script>